import React from "react";
import styled from "styled-components";
import { FormikValues } from "formik";
import { toast } from "react-toastify";
import _ from "lodash";

import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import Grid from "src/library/components/Hedron";
import {
  Form,
  IFormField
} from "src/library/components/Forms";
import {
  IPendingCustomer,
  usePendingCustomerApi
} from "src/core/pending-customers/api/pendingCustomer.api";
import PendingCustomerSidebar, { PendingCustomerSidebarUpdate } from "src/core/pending-customers/components/PendingCustomerSidebar";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import useGenerateCustomerFormFields from "src/core/pending-customers/hooks/useGenerateCustomerFormFields";

type UrlParams = {
  pendingCustomerId: string;
};

/**
 * @description Renders a form to edit pending customers and a sidebar to view and update some pending customer attributes.
 */
const EditPendingCustomerPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { match, terminology } = props;
  const [ pendingCustomer, setPendingCustomer ] = React.useState<IPendingCustomer | null>(null); // store customer
  const [ updatePendingCustomerResponse, updatePendingCustomer ] = usePendingCustomerApi("UPDATE_PENDING_CUSTOMER"); // update customer
  const [ getPendingCustomerResponse, getPendingCustomer ] = usePendingCustomerApi("GET_PENDING_CUSTOMER"); // fetch customer
  const [ formFields, setFormFields ] = React.useState<IFormField[]>([]);
  const [ newCustomerImportSettingRes, newCustomerImportSettingReq ] = useImportSettingApi("GET_IMPORT_SETTING");
  const [ error, setError ] = React.useState("");
  const { generateFormFields } = useGenerateCustomerFormFields();
  const pendingCustomerIdd = match ? parseInt(match.params.pendingCustomerId, 0) : 0;

  // fetch pending customer import settings
  React.useEffect(() => {
    newCustomerImportSettingReq({
      type: ImportSettingType.PendingCustomer,
      origin: ImportOrigin.FORM
    });
  }, [ newCustomerImportSettingReq ]);

  // fetch existing pending customer details
  React.useEffect(() => {
    if (pendingCustomerIdd) {
      getPendingCustomer({ id: pendingCustomerIdd });
    }
  }, [ getPendingCustomer, pendingCustomerIdd ]);

  // Generate fields on import setting fetch
  React.useEffect(() => {
    if (newCustomerImportSettingRes.data && getPendingCustomerResponse.data) {
      // Shape existing customer data such that the 'postcode' key is transformable to the backend key for 'PostCode'
      const transformedResponseData = {
        ...getPendingCustomerResponse.data,
        address: getPendingCustomerResponse.data.address && {
          ...getPendingCustomerResponse.data.address,
          postCode: getPendingCustomerResponse.data.address?.postcode
        }
      };

      const generatedFormFields = generateFormFields(newCustomerImportSettingRes.data, transformedResponseData);

      setFormFields(generatedFormFields);
    }
  }, [
    generateFormFields,
    getPendingCustomerResponse.data,
    newCustomerImportSettingRes.data
  ]);

  // Handle errors from pending customer and import setting fetches
  React.useEffect(() => {
    if (newCustomerImportSettingRes.error) {
      setError(newCustomerImportSettingRes.error.message);
    } else if (
      getPendingCustomerResponse.error
    ) {
      setError(getPendingCustomerResponse.error.message);
    }
  }, [ getPendingCustomerResponse.error, newCustomerImportSettingRes.error ]);

  // Handles updates submitted by the sidebar
  const handleSideBarUpdate = React.useCallback((updateType: PendingCustomerSidebarUpdate, updateValue: number | null) => {
    if (pendingCustomer) {
      updatePendingCustomer({
        id: pendingCustomer.id,
        [ updateType ]: updateValue
      });
    }
  }, [ pendingCustomer, updatePendingCustomer ]);

  // load pending user into local state when the fetch resolves
  React.useEffect(() => {
    if (getPendingCustomerResponse.error) {
      toast.error(getPendingCustomerResponse.error.message);
    }

    if (getPendingCustomerResponse.data) {
      setPendingCustomer(getPendingCustomerResponse.data);
      getPendingCustomerResponse.data = null;
    }
  }, [ getPendingCustomerResponse, setPendingCustomer ]);

  // When updatePendingCustomerResponse updates
  React.useEffect(() => {
    if (updatePendingCustomerResponse.error) {
      toast.error(updatePendingCustomerResponse.error.message);
    }

    // reload state with update value
    if (updatePendingCustomerResponse.data) {
      setPendingCustomer(updatePendingCustomerResponse.data);
      toast.success("Successfully updated customer");
    }
  }, [ updatePendingCustomerResponse.error, updatePendingCustomerResponse.data ]);

  // Handle form submit
  const handleSubmit = React.useCallback((fields: FormikValues) => {
    updatePendingCustomer({
      id: pendingCustomer?.id,
      name: fields.Name,
      address: {
        addressLine1: fields.AddressLine1,
        addressLine2: fields.AddressLine2,
        addressLine3: fields.AddressLine3,
        city: fields.City,
        postcode: fields.PostCode
      },
      contactEmail: fields.ContactEmail,
      contactName: fields.ContactName,
      contactNumber: fields.ContactNumber,
      customData: { ...fields }
    });
  }, [ pendingCustomer?.id, updatePendingCustomer ]);

  return (
    <Layout
      {...props}
      title="Edit pending customer"
    >
      <MainContentWrapper fill>
        <FormWrapper>
          {pendingCustomer && !getPendingCustomerResponse.pending && (
            <Form
              error={error}
              submitting={updatePendingCustomerResponse.pending}
              fields={formFields}
              onSubmit={fields => handleSubmit(fields)}
              submitButtonText="Update customer"
              submitButtonAlignment="center"
            />
          )}
        </FormWrapper>

      </MainContentWrapper>

      <InfoBarWrapper>
        <PendingCustomerSidebar
          isLoading={getPendingCustomerResponse.pending}
          data={pendingCustomer || null}
          terminology={terminology}
          handleUpdate={handleSideBarUpdate}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
  margin-bottom: 10px;
  form {
    width: 400px;
  }
`;

export default EditPendingCustomerPage;